/**
 * App colors: reads from CSS theme variables (packages/client/index.css @theme).
 * Single source of truth for both Tailwind classes and inline/JS styling.
 * Fallbacks are used when not in browser (SSR) or before styles are loaded.
 *
 * Usage: import colors from '~/colors'
 * All palette keys: primary, brand, secondary, white, grey10–grey100, blue10/50/100,
 * green10/50/100, orange10–50, red10/50/100, yellow10/50/100, etc.
 */

const VAR_PREFIX = '--color-';

function getRoot(): HTMLElement | null {
  return typeof document !== 'undefined' ? document.documentElement : null;
}

function getThemeColor(cssVarName: string): string {
  const root = getRoot();
  if (!root) return '';
  const value = getComputedStyle(root).getPropertyValue(cssVarName).trim();
  return value || '';
}

/** Map of JS export key -> CSS variable name (without --color- prefix) */
const VAR_NAMES: Record<string, string> = {
  primary: 'primary',
  primaryDark: 'primary-dark',
  primaryLight: 'primary-light',
  primaryForeground: 'primary-foreground',
  brand: 'brand',
  brandDark: 'brand-dark',
  brandLight: 'brand-light',
  secondary: 'secondary',
  secondaryDark: 'secondary-dark',
  secondaryLight: 'secondary-light',
  secondaryForeground: 'secondary-foreground',
  white: 'white',
  foreground: 'foreground',
  destructive: 'destructive',
  blue10: 'blue-10',
  blue20: 'blue-20',
  blue40: 'blue-40',
  blue50: 'blue-50',
  blue60: 'blue-60',
  blue80: 'blue-80',
  blue90: 'blue-90',
  green10: 'green-10',
  green20: 'green-20',
  green50: 'green-50',
  green60: 'green-60',
  grey10: 'grey-10',
  grey20: 'grey-20',
  grey30: 'grey-30',
  grey40: 'grey-40',
  grey50: 'grey-50',
  grey60: 'grey-60',
  grey70: 'grey-70',
  grey80: 'grey-80',
  grey90: 'grey-90',
  slate70: 'slate-70',
  slate80: 'slate-80',
  orange10: 'orange-10',
  orange20: 'orange-20',
  orange40: 'orange-40',
  orange50: 'orange-50',
  orange60: 'orange-60',
  red10: 'red-10',
  red20: 'red-20',
  red50: 'red-50',
  red60: 'red-60',
  yellow10: 'yellow-10',
  yellow20: 'yellow-20',
  yellow50: 'yellow-50',
  yellow60: 'yellow-60',
};

/** Fallbacks when CSS vars are not available (SSR / before load). Must match index.css @theme. */
const FALLBACKS: Record<string, string> = {
  primary: '#243442',
  primaryDark: '#1F2C38',
  primaryLight: '#D3D6D9',
  primaryForeground: '#ffffff',
  brand: '#F27E31',
  brandDark: '#DA712C',
  brandLight: '#FCE5D6',
  secondary: '#656E75',
  secondaryDark: '#333C41',
  secondaryLight: '#EBECED',
  secondaryForeground: '#000000',
  white: '#FFFFFF',
  foreground: '#000000',
  destructive: '#eb5e55',
  red10: '#ffefee',
  red20: '#f5cdcb',
  red50: '#d61e17',
  red60: '#b0140f',
  green10: '#e8fcec',
  green20: '#c8f5d2',
  green50: '#109c0e',
  green60: '#0b7a09',
  grey10: '#f4f5f5',
  grey20: '#e9eaeb',
  grey30: '#d2d4d7',
  grey40: '#babdc0',
  grey50: '#888c90',
  grey60: '#6e7376',
  grey70: '#54585b',
  grey80: '#393b3d',
  grey90: '#1d1e20',
  slate70: '#3c464d',
  slate80: '#333c41',
  orange10: '#ffefe5',
  orange20: '#f7d0ae',
  orange40: '#f49c5e',
  orange50: '#f27e31',
  orange60: '#da712c',
  blue10: '#eaf5ff',
  blue20: '#c0dbfe',
  blue40: '#27b4f6',
  blue50: '#196fe0',
  blue60: '#134a91',
  blue80: '#243442',
  blue90: '#1f2c38',
  yellow10: '#fffbef',
  yellow20: '#ffefbb',
  yellow50: '#f2bc46',
  yellow60: '#db9c13',
};

function read(key: string): string {
  const varName = VAR_NAMES[key];
  if (!varName) return FALLBACKS[key] ?? '';
  const value = getThemeColor(VAR_PREFIX + varName);
  return value || (FALLBACKS[key] ?? '');
}

const themeColors = new Proxy({} as Record<string, string>, {
  get(_, prop: string) {
    return read(prop);
  },
  has(_, prop: string) {
    return prop in VAR_NAMES || prop in FALLBACKS;
  },
});

export default themeColors;

/**
 * Static project-area colors — the single source of truth for the user's OWN project
 * boundary (line + fill + legend swatch) across every page/tab in the client.
 * Linked-org/underlay projects are intentionally excluded (they stay blue50).
 */
export const PROJECT_AREA_LINE_COLOR = themeColors.brand; // #F27E31 (unselected)
export const PROJECT_AREA_SELECTED_LINE_COLOR = themeColors.primaryDark; // #1F2C38 (selected)
export const PROJECT_AREA_HOVER_LINE_COLOR = themeColors.secondary;
export const PROJECT_AREA_FILL_COLOR = themeColors.brand;
